import React from "react"
import JSZip from "jszip"
import * as httpx from "httpx"
import * as layouts from "layouts"
import * as icons from "icons"
import * as media from "media"
import * as mediaapi from "media/api"
import * as api from "brandguard/api"
import * as unified from "brandguard/display/unified"
import * as scores from "brandguard/components/layouts/scores"
import * as authz from "authz"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import * as bglayouts from "./layouts"
import { FilterDisplay, Checkbox, CalculateFilterSCount } from "./filter"

export const defaultLimit = 20n

export function sameMedia(a: unified.UnifiedItem, b: unified.UnifiedItem | undefined): boolean {
	return !!b && a.data.media?.id === b.data.media?.id
}

// singleEventType returns the event_type shared by every item in the list, or
// undefined when the list is empty or mixed across types. Feedback endpoints
// are per-type, so bulk feedback is only meaningful within a single type.
export function singleEventType(items: unified.UnifiedItem[]): unified.AssetType | undefined {
	if (items.length === 0) return undefined
	const first = items[0].event_type
	return items.every((i) => i.event_type === first) ? first : undefined
}

// singleUploader returns the profile that uploaded every item in the list, or
// undefined when the list is empty or the items have different uploaders.
export function singleUploader(items: unified.UnifiedItem[]): string | undefined {
	const first = items[0]?.data.media?.uploaded_by
	return items.every((i) => i.data.media?.uploaded_by === first) ? first : undefined
}

interface scoreProps {
	item: unified.UnifiedItem
}

export function CardScore(props: scoreProps): JSX.Element {
	const { item } = props
	const settings = useSettings()
	if (!item.data.event) return <></>
	const inner = (() => {
		switch (item.event_type) {
			case "image":
				return <scores.BGScoreImageSimple event={item.data.event} />
			case "text":
				return <scores.BGScoreTextSimple event={item.data.event} />
			case "video":
				return <scores.BGScoreVideoSimple event={item.data.event} />
			case "document":
				return <></>
		}
	})()
	return (
		<authz.Protected enabled={settings.confidence !== State.DISABLED}>
			<layouts.containers.absolute>{inner}</layouts.containers.absolute>
		</authz.Protected>
	)
}

interface downloadProps {
	items: unified.UnifiedItem[]
	accountID: string
	bearertoken: httpx.option
}

// downloadUnified writes selected items to a single .zip (binary types) and a
// concatenated .csv (text). Mixed selections produce both files.
export function downloadUnified(props: downloadProps): void {
	const { items, accountID, bearertoken } = props
	const texts = items.filter((i) => i.event_type === "text")
	const binaries = items.filter((i) => i.event_type !== "text")
	if (binaries.length > 0) {
		const zip = new JSZip()
		const jobs = binaries.map((i) =>
			media.cache.current(accountID, i.data.media!.md5, bearertoken).then((url) =>
				fetch(url)
					.then((r) => r.arrayBuffer())
					.then((buf) => zip.file(media.downloads.filename(i.data.media!.md5, i.data.media!.mimetype), buf)),
			),
		)
		Promise.all(jobs).then(() => {
			zip.generateAsync({ type: "blob" }).then((content) => {
				const link = document.createElement("a")
				link.href = URL.createObjectURL(content)
				link.download = "brandguard-assets.zip"
				link.click()
			})
		})
	}
	if (texts.length > 0) {
		const jobs = texts.map((i) =>
			mediaapi.url(i.data.media!.md5, bearertoken).then((r) => fetch(r.url).then((resp) => resp.text())),
		)
		Promise.all(jobs).then((content) => {
			const link = document.createElement("a")
			link.href = URL.createObjectURL(new Blob([content.join("\n")], { type: "text/csv" }))
			link.download = "brandguard-assets.csv"
			link.click()
			link.remove()
		})
	}
}

interface removeProps {
	items: unified.UnifiedItem[]
	bearer: httpx.option
}

// Filter fields shared across the four search-request types. approved is only
// meaningful in the uploads/review states; the four filters below cover the
// same fields the per-type files previously exposed.
export interface FilterOverride {
	uploaded_by: string[]
	approved: api.Prediction[]
	observation: api.Prediction[]
	confidence: api.FloatRange
}

export function applyOverride<T extends api.ImageSearchRequest | api.TextSearchRequest | api.DocumentSearchRequest | api.VideoSearchRequest>(
	base: T,
	override: Partial<FilterOverride>,
): T {
	return { ...base, ...override }
}

interface unifiedFilterProps {
	baseline: api.ImageSearchRequest
	current: api.ImageSearchRequest
	focused: boolean
	onToggle(): void
	onClear(): void
	onChange(next: Partial<FilterOverride>): void
	showApprovedRejectedCheckboxes: boolean
	assetTypesSelected: Set<unified.AssetType>
	onAssetTypesChange(next: Set<unified.AssetType>): void
}

// UnifiedFilterButton wraps the shared FilterButton + FilterDisplay chrome in a
// single component so the four unified state files render an identical filter
// row. The approved/rejected observation checkboxes are optional because they
// only make sense in the uploads/review states.
export function UnifiedFilterButton(props: unifiedFilterProps): JSX.Element {
	const {
		baseline,
		current,
		focused,
		onToggle,
		onClear,
		onChange,
		showApprovedRejectedCheckboxes,
		assetTypesSelected,
		onAssetTypesChange,
	} = props
	const fcnt = CalculateFilterSCount(baseline, current) + (assetTypesSelected.size > 0 ? 1 : 0)
	const appchecked = current.approved.length === 1 && current.approved[0] === api.Prediction.APPROVED
	const rejchecked = current.approved.length === 1 && current.approved[0] === api.Prediction.REJECTED
	return (
		<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
			<layouts.buttons.outline
				width="150px"
				height="40px"
				borderRadius="4px"
				onClick={onToggle}
				className={fcnt > 0 ? "active" : ""}
			>
				<layouts.containers.flex flexDirection="row" alignItems="center" justifyContent="center">
					<icons.bgapproval.filter />
					<layouts.containers.box px="10px">Filter{fcnt > 0 ? `/${fcnt}` : ""}</layouts.containers.box>
					<icons.bgapproval.arrowDown />
				</layouts.containers.flex>
			</layouts.buttons.outline>
			<layouts.containers.flex display={focused ? "flex" : "none"} position="absolute" top="100%" pt="5px">
				<FilterDisplay
					onFilterClear={onClear}
					_searchReq={baseline}
					onFilterChange={(r) => {
						const patch: Partial<FilterOverride> = {}
						if (r.uploaded_by !== undefined) patch.uploaded_by = r.uploaded_by
						if (r.confidence !== undefined) patch.confidence = r.confidence
						onChange(patch)
					}}
					assetTypesSelected={assetTypesSelected}
					onAssetTypesChange={onAssetTypesChange}
				>
					{showApprovedRejectedCheckboxes && (
						<>
							<Checkbox
								description="Assets that were approved"
								checked={appchecked}
								onChange={() =>
									onChange({ approved: appchecked ? baseline.approved : [api.Prediction.APPROVED] })
								}
							/>
							<Checkbox
								description="Assets that were rejected"
								checked={rejchecked}
								onChange={() =>
									onChange({ approved: rejchecked ? baseline.approved : [api.Prediction.REJECTED] })
								}
							/>
						</>
					)}
				</FilterDisplay>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}

interface uploadButtonProps {
	onToggle(): void
	panel: JSX.Element
}

// UploadButton renders the outline "Upload Assets" button with the caller's upload
// panel docked beneath it; the caller opens the panel that accepts every asset type.
export function UploadButton(props: uploadButtonProps): JSX.Element {
	const { onToggle, panel } = props
	return (
		<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative" mr="10px">
			<layouts.buttons.outline width="150px" height="40px" borderRadius="4px" onClick={onToggle}>
				<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.blue.blue} />
				<layouts.Span px="10px">Upload Assets</layouts.Span>
			</layouts.buttons.outline>
			<layouts.containers.flex position="absolute" top="100%" pt="5px">
				{panel}
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}

// tombstoneUnified soft-deletes each selected item via its type-specific
// uploads.patch. Returns the set of media ids that were successfully removed.
export async function tombstoneUnified(props: removeProps): Promise<string[]> {
	const { items, bearer } = props
	const now = new Date().toISOString()
	return Promise.all(
		items.map((i) => {
			switch (i.event_type) {
				case "image": {
					const d = { ...i.data, media: { ...i.data.media!, tombstoned_at: now } }
					return api.images.uploads.patch(d.media.brand_id, d.media.id, d, bearer).then((r) => r.media!.id)
				}
				case "text": {
					const d = { ...i.data, media: { ...i.data.media!, tombstoned_at: now } }
					return api.text.uploads.patch(d.media.brand_id, d.media.id, d, bearer).then((r) => r.media!.id)
				}
				case "document": {
					const d = { ...i.data, media: { ...i.data.media!, tombstoned_at: now } }
					return api.documents.uploads.patch(d.media.brand_id, d.media.id, d, bearer).then((r) => r.media!.id)
				}
				case "video": {
					const d = { ...i.data, media: { ...i.data.media!, tombstoned_at: now } }
					return api.videos.uploads.patch(d.media.brand_id, d.media.id, d, bearer).then((r) => r.media!.id)
				}
			}
		}),
	)
}
